/**
 * Sessie 53: kosten per mailconcept, veertien dagen, tegen het doel.
 *
 * Per dag het aantal concepten, het gemiddelde en het totaal; per
 * vraagtype het gemiddelde. Elke kleur heeft een woord ernaast ("onder
 * doel", "boven doel"). Een blok dat niet laadt zegt dat zelf.
 *
 * Motor sessie 8H: de mailbeurten van V8 tellen per dag mee; de regel
 * Opbouw zegt welk deel van de oude motor is en welk van V8. Per
 * vraagtype alleen de oude motor (V8 meldt geen vraagtype).
 *
 * Sessie 53 blok 1 (G2): het blok staat op een eigen rij over de volle
 * breedte onder de kolommen. Bedragen breken nooit; op een smal scherm
 * valt de kolom Totaal weg (dag, aantal, gemiddeld en de chip blijven).
 */
import { useQuery } from "@tanstack/react-query";

import {
  mailconceptKostenApi,
  type MailconceptStand,
} from "@/lib/api";

const CHIPKLASSE: Record<string, string> = {
  goed: "wp-chip wp-chip-whatsapp",
  let_op: "wp-chip wp-chip-risico",
};

function euro(waarde: number | null): string {
  if (waarde === null) return "geen";
  return waarde.toLocaleString("nl-NL", { style: "currency", currency: "EUR" });
}

function dagLabel(datum: string): string {
  return new Date(`${datum}T12:00:00`).toLocaleDateString("nl-NL", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });
}

function Chip({ stand, chip }: { stand: MailconceptStand; chip: string | null }) {
  if (!chip) return null;
  return (
    <span className={(stand && CHIPKLASSE[stand]) || "wp-chip wp-chip-grijs"}>{chip}</span>
  );
}

export function MailconceptKosten() {
  const kosten = useQuery({
    queryKey: ["werkplek", "gezondheid", "mailkosten"],
    queryFn: mailconceptKostenApi.haal,
  });
  const data = kosten.data;

  return (
    <div className="wp-blok wp-gz-mk" data-testid="mailconcept-kosten">
      <h4>
        <span>Kosten per mailconcept</span>
        <span className="wp-gz-mk-onder">
          14 dagen · doel <span className="wp-gz-mk-bedrag">{euro(data?.doel_eur ?? 0.1)}</span>
        </span>
      </h4>
      {kosten.isLoading ? (
        <p className="wp-gz-uitleg">Bezig met laden…</p>
      ) : kosten.isError || !data ? (
        <p className="wp-gz-uitleg" style={{ color: "var(--wp-rood)" }}>
          De kosten per mailconcept zijn niet op te halen.
        </p>
      ) : data.aantal === 0 ? (
        <p className="wp-gz-uitleg" role="status">
          Geen mailconcepten in deze 14 dagen.
        </p>
      ) : (
        <>
          <p className="wp-gz-mk-samen">
            <b className="wp-gz-mk-bedrag">{euro(data.gemiddeld_eur)}</b>
            <span>gemiddeld · {data.aantal} concepten</span>
            <span>
              · totaal <span className="wp-gz-mk-bedrag">{euro(data.totaal_eur)}</span>
            </span>
            <Chip stand={data.stand} chip={data.chip} />
          </p>
          {/* Motor sessie 8H: de opbouw, oude motor en V8 apart. */}
          {data.opbouw && data.opbouw.length > 0 ? (
            <p className="wp-gz-mk-samen" data-testid="mailconcept-opbouw">
              <span>Opbouw:</span>
              {data.opbouw.map((deel, index) => (
                <span key={deel.motor}>
                  {index > 0 ? "· " : ""}
                  {deel.label} {deel.aantal} {deel.aantal === 1 ? "concept" : "concepten"}
                  {deel.aantal > 0 ? (
                    <>
                      {", "}
                      <span className="wp-gz-mk-bedrag">{euro(deel.totaal_eur)}</span>
                    </>
                  ) : null}
                </span>
              ))}
            </p>
          ) : null}
          <div className="wp-gz-mk-tabellen">
            <table className="wp-gz-mk-tabel">
              <thead>
                <tr>
                  <th>Dag</th>
                  <th className="wp-gz-mk-getal">Aantal</th>
                  <th className="wp-gz-mk-getal">Gemiddeld</th>
                  <th className="wp-gz-mk-getal wp-gz-mk-totaal">Totaal</th>
                  <th className="wp-gz-mk-stand" aria-label="Stand" />
                </tr>
              </thead>
              <tbody>
                {data.dagen.map((dag) => (
                  <tr key={dag.datum}>
                    <td className="wp-gz-mk-dag">{dagLabel(dag.datum)}</td>
                    <td className="wp-gz-mk-getal">{dag.aantal}</td>
                    <td className="wp-gz-mk-getal wp-gz-mk-bedrag">
                      {dag.aantal ? euro(dag.gemiddeld_eur) : "–"}
                    </td>
                    <td className="wp-gz-mk-getal wp-gz-mk-bedrag wp-gz-mk-totaal">
                      {dag.aantal ? euro(dag.totaal_eur) : "–"}
                    </td>
                    <td className="wp-gz-mk-stand">
                      <Chip stand={dag.stand} chip={dag.chip} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <table className="wp-gz-mk-tabel">
              <thead>
                <tr>
                  <th>Vraagtype (oude motor)</th>
                  <th className="wp-gz-mk-getal">Aantal</th>
                  <th className="wp-gz-mk-getal">Gemiddeld</th>
                  <th className="wp-gz-mk-stand" aria-label="Stand" />
                </tr>
              </thead>
              <tbody>
                {data.per_vraagtype.map((rij) => (
                  <tr key={rij.vraagtype ?? "geen"}>
                    <td>{rij.vraagtype ? rij.vraagtype.replace(/_/g, " ") : "zonder vraagtype"}</td>
                    <td className="wp-gz-mk-getal">{rij.aantal}</td>
                    <td className="wp-gz-mk-getal wp-gz-mk-bedrag">{euro(rij.gemiddeld_eur)}</td>
                    <td className="wp-gz-mk-stand">
                      <Chip stand={rij.stand} chip={rij.chip} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
